import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  useToast,
  Collapse
} from "@chakra-ui/react";
import * as XLSX from "xlsx";
import { createWorkbookFromTable, textCellAttributes } from "../../utils/table_export";
import { saveAs } from "file-saver";
import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../store/store_context";
import DateFilters from "./date_filters";
import ColumnValueFilter, { HeaderTitle, ResetColumnFiltersButton, useColumnFilters } from "../column_value_filter";
import { defaultCellText, rowsPassing } from "../../utils/column_value_filter";

const PotentialTable = observer(({ data, renderButtons }) => {
  const toast = useToast();

  const [groupedData, setGroupedData] = useState([]);

  const { pageStore } = useStores();

  const cleanNumber = (value) => {
    if (typeof value === "string") {
      const cleaned = value.replace(/[^\d,.-]/g, "").replace(",", ".");
      return parseFloat(cleaned) || 0;
    }
    return parseFloat(value) || 0;
  };

  function filterByDateRange(data, filterType, store, field, customStart, customEnd) {
    const currentDate = new Date();
    let start = null;
    let end = null;

    switch (filterType) {
      case "fill":
        return data.filter(item => item[field]);
      case "empty":
        return data.filter(item => !item[field]);
      case "week":
        const monday = getMonday(currentDate);
        start = store.toISODateString(monday);
        end = store.toISODateString(new Date(monday.getTime() + 6 * 86400000));
        break;
      case "month":
        start = store.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        end = store.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
        break;
      case "pastMonth":
        start = store.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        end = store.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 0));
        break;
      case "quarter":
        const quarter = Math.floor(currentDate.getMonth() / 3);
        start = store.toISODateString(new Date(currentDate.getFullYear(), quarter * 3, 1));
        end = store.toISODateString(new Date(currentDate.getFullYear(), quarter * 3 + 3, 0));
        break;
      case "halfYear":
        const half = Math.floor(currentDate.getMonth() / 6);
        start = store.toISODateString(new Date(currentDate.getFullYear(), half * 6, 1));
        end = store.toISODateString(new Date(currentDate.getFullYear(), half * 6 + 6, 0));
        break;
      case "year":
        start = store.toISODateString(new Date(currentDate.getFullYear(), 0, 1));
        end = store.toISODateString(new Date(currentDate.getFullYear(), 11, 31));
        break;
      case "pastYear":
        start = store.toISODateString(new Date(currentDate.getFullYear() - 1, 0, 1));
        end = store.toISODateString(new Date(currentDate.getFullYear() - 1, 11, 31));
        break;
      case "custom":
        start = customStart;
        end = customEnd;
        break;
      default:
        break;
    }

    if (start && end) {
      return data.filter(item => item[field] && item[field] >= start && item[field] <= end);
    }

    return data;
  }

  const processedData = React.useMemo(() => {
    return data.map((item) => {
      const RevRub = cleanNumber(item.RevRub);
      const PaidRub = cleanNumber(item.PaidRub);
      const profit = cleanNumber(item.profit);
      const plan_rent = cleanNumber(item.plan_rent)
      const UF_CRM_1756460743245 = cleanNumber(item.UF_CRM_1756460743245);

      const openProfitValue = UF_CRM_1756460743245 || 0;

      const profitabilityFact = profit && RevRub ? (profit / RevRub) * 100 : 0;
      const profitabilityOpen =
        openProfitValue && RevRub ? (openProfitValue / RevRub) * 100 : 0;
      const profitInCash = profit
        ? PaidRub * (profitabilityFact / 100)
        : (openProfitValue / RevRub) * PaidRub
      let openProfit =
        openProfitValue && !item.ClosDate
          ? openProfitValue - profitInCash
          : 0;
      let totalProfit = openProfit + profitInCash;

      if (item.ClosDate && profit == 0) {
        openProfit = openProfitValue;
        totalProfit = openProfitValue;
      }

      return {
        ...item,
        TITLE: item.TITLE ?? "",
        RevRub,
        PaidRub,
        profit,
        openProfitValue,
        profitabilityFact,
        profitabilityOpen,
        profitInCash: profitInCash || 0,
        openProfit: openProfit || 0,
        totalProfit: totalProfit || 0,
      };
    });
  }, [data]);

  const columns = processedData.length > 0 ? Object.keys(processedData[0]) : [];

  const availableColumns = pageStore.columnConfigPotential.filter(
    (col) => processedData.length > 0 && col.active
  );

  const renderCellContent = (column, value) => {
    if (column.isLink && value) {
      const href = column.linkTemplate.replace("{value}", value);
      return (
        <Link
          href={href}
          isExternal
          textDecoration={"underline"}
          color="white"
          _hover={{ color: "blue.500" }}
        >
          {value}
        </Link>
      );
    }

    return value || "";
  };

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.getFullYear(), date.getMonth(), diff);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "totalProfit",
    direction: "desc",
  });
  const [filterText, setFilterText] = useState("");

  const expandAll = () => {
    const allExpanded = {};
    groupedData?.forEach((group) => {
      allExpanded[group.key] = true;
      group.subGroups?.forEach((subGroup) => {
        if (subGroup.key != null) {
          allExpanded[`${group.key}__${subGroup.key}`] = true;
        }
      });
    });
    setExpandedGroups(allExpanded);
  };

  const collapseAll = () => {
    setExpandedGroups({});
  };

  // Строки после фильтров страницы — база для списков значений «⏷».
  const preColumnData = React.useMemo(() => {
    // ✅ Копируем данные один раз
    let sortableData = [...processedData];

    // === 🔹 1. Фильтр по номеру ===
    const orderNum = pageStore.order_num_filter?.toLowerCase();
    if (orderNum) {
      sortableData = sortableData.filter(
        (item) =>
          item.UF_CRM_1486711068 &&
          item.UF_CRM_1486711068.toLowerCase().includes(orderNum)
      );
    }

    if (pageStore.order_deal_filter) {
      sortableData = sortableData.filter(
        (item) => item.ID && item.ID.toString().includes(pageStore.order_deal_filter)
      )
    }

    if (pageStore.isFail) {
      sortableData = sortableData.filter(
        (item) =>
          item.semantics == "F"
      )
    } else {
      sortableData = sortableData.filter(
        (item) =>
          item.semantics != "F"
      )
    }

    if (
      pageStore.group_by_potential === "departament-withoutROP" ||
      pageStore.group_by_potential === "departament-manager-withoutROP"
    ) {
      sortableData = sortableData.filter(
        (item) =>
          item.role_name != "Руководитель отдела продаж" && item.role_name != "Руководитель удалённого отдела продаж"
      )
    }

    if (pageStore.workers) {
      sortableData = sortableData.filter(
        (item) =>
          item.active
      )
    }

    const startType = pageStore.order_type_date_start_filter;
    const endType = pageStore.order_type_date_end_filter;

    if (startType && startType !== "none") {
      sortableData = filterByDateRange(
        sortableData,
        startType,
        pageStore,
        "AccOpenDate",
        pageStore.order_start_open_date_filter,
        pageStore.order_end_open_date_filter
      );
    }

    if (endType && endType !== "none") {
      sortableData = filterByDateRange(
        sortableData,
        endType,
        pageStore,
        "ClosDate",
        pageStore.order_start_close_date_filter,
        pageStore.order_end_close_date_filter
      );
    }

    if (sortConfig?.key && !pageStore.group_by_potential) {
      const { key, direction } = sortConfig;

      sortableData.sort((a, b) => {
        const A = a[key];
        const B = b[key];

        if (typeof A === "number" && typeof B === "number") {
          return direction === "asc" ? A - B : B - A;
        }

        if ((key === "AccOpenDate" || key === "ClosDate") && A && B) {
          return direction === "asc"
            ? new Date(A) - new Date(B)
            : new Date(B) - new Date(A);
        }

        return direction === "asc"
          ? String(A ?? "").localeCompare(String(B ?? ""))
          : String(B ?? "").localeCompare(String(A ?? ""));
      });
    }

    return sortableData;
  }, [
    processedData,
    sortConfig,
    pageStore.order_num_filter,
    pageStore.order_type_date_start_filter,
    pageStore.order_type_date_end_filter,
    pageStore.order_start_open_date_filter,
    pageStore.order_end_open_date_filter,
    pageStore.order_start_close_date_filter,
    pageStore.order_end_close_date_filter,
    pageStore.isFail,
    pageStore.columnConfigPotential,
    pageStore.group_by_potential,
    pageStore.workers,
    pageStore.order_deal_filter
  ]);

  // Аналитический фильтр «⏷» по значениям, как в «Просроченной ДЗ»: у всех
  // столбцов. Значения — как в ячейках: суммы округлены, рентабельность в %.
  const potentialCellText = React.useCallback((row, key) => {
    const column = pageStore.columnConfigPotential.find((col) => col.key === key);
    const value = row?.[key];
    if (column?.isNumeric && typeof value === "number") {
      return column.isPercentage ? `${value.toFixed(2)}%` : Math.round(value).toLocaleString("ru-RU");
    }
    return defaultCellText(row, key);
  }, [pageStore.columnConfigPotential]);
  const [columnFilters, setColumnFilters] = useColumnFilters(
    processedData,
    availableColumns.map((col) => col.key)
  );
  const sortedData = React.useMemo(
    () => rowsPassing(preColumnData, columnFilters, potentialCellText),
    [preColumnData, columnFilters, potentialCellText]
  );
  const renderValueFilter = (column) => (
    <ColumnValueFilter
      columnKey={column.key}
      title={column.title}
      rows={preColumnData}
      filters={columnFilters}
      onChange={setColumnFilters}
      textOf={potentialCellText}
    />
  );

  const calculateTotals = useMemo(() => {
    const totals = {};
    const uniqueManagers = new Set();
    const uniqueCompanies = new Set();
    const uniqueProducers = new Set();

    availableColumns.forEach(column => {
      if (column.isNumeric) {
        totals[column.key] = 0;
      }
    });

    sortedData.forEach(item => {
      availableColumns.forEach(column => {
        if (column.isNumeric && !column.isPercentage) {
          totals[column.key] += item[column.key] || 0;
        }
      });
      if (item.fio) uniqueManagers.add(item.fio);
      if (item.name) uniqueCompanies.add(item.name);
      if (item.UF_CRM_1473868262) uniqueProducers.add(item.UF_CRM_1473868262);
    });

    totals['profitabilityFact'] = totals['RevRub']
      ? (totals['profit'] / totals['RevRub']) * 100
      : 0;
    totals['profitabilityOpen'] = totals['RevRub']
      ? (totals['openProfitValue'] / totals['RevRub']) * 100
      : 0;

    totals.count = sortedData.length;
    totals.uniqueManagersCount = uniqueManagers.size;
    totals.uniqueCompaniesCount = uniqueCompanies.size;
    totals.uniqueProducersCount = uniqueProducers.size;

    return totals;
  }, [sortedData, availableColumns]);

  const formatCellValue = (value, isNumeric = false, isPercentage = false) => {
    if ((value === null || value === undefined || value === "") && isNumeric) {
      return "0";
    }

    if (typeof value === "number") {
      if (isPercentage) {
        return `${value.toFixed(2)}%`;
      }
      return Math.round(value).toLocaleString("ru-RU");
    }
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }
    return value || "";
  };

  const renderGroupCellContent = (column, totals) => {
    switch (column.key) {
      case "ID":
      case "TITLE":
        return `${totals.count ?? 0}`;
      case "fio":
        return `${totals.uniqueManagersCount ?? 0}`;
      case "name":
        return `${totals.uniqueCompaniesCount ?? 0}`;
      case "UF_CRM_1473868262":
        return `${totals.uniqueProducersCount ?? 0}`;
      default:
        return "";
    }
  };

  const renderSummaryCell = (column, totals) => {
    if (column.isNumeric) {
      return formatCellValue(
        totals[column.key],
        column.isNumeric,
        column.isPercentage
      );
    }
    return renderGroupCellContent(column, totals);
  };

  const handleExportToXLSX = async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createWorkbookFromTable(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");
  };

  const [expandedGroups, setExpandedGroups] = useState({});

  const calculateGroupTotals = (items) => {
    const totals = {};
    const uniqueManagers = new Set();
    const uniqueCompanies = new Set();
    const uniqueProducers = new Set();

    availableColumns.forEach(column => {
      if (column.isNumeric) {
        totals[column.key] = 0;
      }
    });

    items.forEach(item => {
      availableColumns.forEach(column => {
        if (column.isNumeric && !column.isPercentage) {
          totals[column.key] += item[column.key] || 0;
        }
      });
      if (item.fio) uniqueManagers.add(item.fio);
      if (item.name) uniqueCompanies.add(item.name);
      if (item.UF_CRM_1473868262) uniqueProducers.add(item.UF_CRM_1473868262);
    });

    totals['profitabilityFact'] = totals['RevRub']
      ? (totals['profit'] / totals['RevRub']) * 100
      : 0;
    totals['profitabilityOpen'] = totals['RevRub']
      ? (totals['openProfitValue'] / totals['RevRub']) * 100
      : 0;

    totals.count = items.length;
    totals.uniqueManagersCount = uniqueManagers.size;
    totals.uniqueCompaniesCount = uniqueCompanies.size;
    totals.uniqueProducersCount = uniqueProducers.size;

    return totals;
  };

  const getPrimaryKey = (item, groupBy) => {
    switch (groupBy) {
      case "manager":
      case "manager-deals":
      case "manager-partner":
      case "manager-producer":
        return item.fio || "Без менеджера";
      case "manufacturer":
      case "producer-producer":
      case "producer-manager":
        return item.UF_CRM_1473868262 || "Без производителя";
      case "org":
      case "partner-partner":
      case "partner-manager":
      case "partner-deals":
        return item.name || "Без контрагента";
      case "departament":
      case "departament-withoutROP":
      case "departament-manager":
      case "departament-manager-withoutROP":
        return item.departament_name || "Без отдела";
      case "deal":
        return item.TITLE || item.ID || "Без сделки";
      default:
        return "Все";
    }
  };

  const getSecondaryKey = (item, groupBy) => {
    switch (groupBy) {
      case "manager-partner":
        return item.name || "Без контрагента";
      case "partner-manager":
        return item.fio || "Без менеджера";
      case "manager-producer":
        return item.UF_CRM_1473868262 || "Без производителя";
      case "producer-manager":
        return item.fio || "Без менеджера";
      case "departament-manager":
      case "departament-manager-withoutROP":
        return item.fio || "Без менеджера";
      default:
        return null;
    }
  };

  const hasSecondaryLevel = (groupBy) => {
    return [
      "manager-partner",
      "partner-manager",
      "manager-producer",
      "producer-manager",
      "departament-manager",
      "departament-manager-withoutROP",
    ].includes(groupBy);
  };

  const groupData = (data, groupBy) => {
    const grouped = {};

    data.forEach((item) => {
      const primaryKey = getPrimaryKey(item, groupBy);
      const secondaryKey = getSecondaryKey(item, groupBy);

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = { items: [], subGroups: {} };
      }

      grouped[primaryKey].items.push(item);

      if (secondaryKey != null) {
        if (!grouped[primaryKey].subGroups[secondaryKey]) {
          grouped[primaryKey].subGroups[secondaryKey] = [];
        }
        grouped[primaryKey].subGroups[secondaryKey].push(item);
      }
    });

    const result = Object.entries(grouped).map(([key, group]) => {
      const subGroups = Object.entries(group.subGroups).map(([subKey, items]) => ({
        key: subKey,
        items,
        totals: calculateGroupTotals(items),
      }));

      return {
        key,
        items: group.items,
        totals: calculateGroupTotals(group.items),
        subGroups,
        hasSubGroups: hasSecondaryLevel(groupBy) && subGroups.length > 0,
      };
    });

    if (sortConfig?.key) {
      const { key: sortKey, direction } = sortConfig;

      const compareByTotals = (a, b) => {
        const A = a.totals?.[sortKey] ?? 0;
        const B = b.totals?.[sortKey] ?? 0;
        return direction === "asc" ? A - B : B - A;
      };

      result.sort(compareByTotals);
      result.forEach((group) => {
        group.subGroups.sort(compareByTotals);
      });
    }

    return result;
  };

  useEffect(() => {
    if (pageStore.group_by_potential) {
      setGroupedData(groupData(sortedData, pageStore.group_by_potential));
    } else {
      setGroupedData([]);
    }
  }, [sortedData, pageStore.group_by_potential, sortConfig]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };


  const user = pageStore.all_managers?.find(
    (item) => item?.email == pageStore.email
  );


  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === "asc" ? "desc" : "asc"
          : "desc",
    }));
  };

  const buttons = (
    <>
      <Button
        onClick={handleExportToXLSX}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Экспорт в Excel
      </Button>
      <Button
        onClick={expandAll}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Развернуть всё
      </Button>
      <Button
        onClick={collapseAll}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
      >
        Свернуть всё
      </Button>
    </>
  )

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>
      {pageStore.data_potentials.length ?
        <Box w="100%">

          <DateFilters />
          <ResetColumnFiltersButton filters={columnFilters} onReset={() => setColumnFilters({})} />
          <Box
            maxHeight="95vh"
            overflowX="auto"
            borderRadius="md"
            borderWidth={1}
            borderColor="gray.300"
          >


            {pageStore.group_by_potential ? (
              <Table
                tableLayout="auto"
                variant="simple"
                color="white"
                width="auto"
                maxWidth="fit-content"
              >

                <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
                  <Tr>
                    <Th color={"white"} width="100px" py={pageStore.rowHeight}>Действия</Th>
                    <Th color={"white"} py={pageStore.rowHeight}>Группа</Th>
                    {availableColumns.map((column) => (
                      <Th
                        key={column.key}
                        color={"white"}
                        onClick={() => handleSort(column.key)}
                        px={4}
                        py={pageStore.rowHeight}
                        textAlign={column.isNumeric ? "right" : "left"}
                      >
                        <HeaderTitle
                          title={column.title}
                          suffix={sortConfig.key === column.key && (
                            <Box as="span" ml={2}>
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </Box>
                          )}
                          filter={renderValueFilter(column)}
                        />
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {/* Общие итоги */}
                  <Tr bg="blue.700" fontWeight="bold">
                    <Td colSpan={2} py={pageStore.rowHeight}>Итого {sortedData.length}</Td>
                    {availableColumns.map((column) => {
                      const isNumeric = column.isNumeric || false;

                      return (
                        <Td
                          key={column.key}
                          px={4}
                          py={pageStore.rowHeight}
                          textAlign={isNumeric ? "right" : "left"}
                        >
                          {renderSummaryCell(column, calculateTotals)}
                        </Td>
                      );
                    })}
                  </Tr>

                  {groupedData.map((group) => (
                    <React.Fragment key={group.key}>
                      {/* Строка с итогами группы (уровень 1) */}
                      <Tr bg="#333">
                        <Td>
                          <Button
                            color="white"
                            fontSize="20px"
                            _hover={{ bg: "#222", color: "white" }}
                            size="sm"
                            bgColor="#333"
                            onClick={() => toggleGroup(group.key)}
                          >
                            {expandedGroups[group.key] ? "−" : "+"}
                          </Button>
                        </Td>
                        <Td
                          cursor="pointer"
                          onClick={() => toggleGroup(group.key)}
                          fontWeight="bold"
                          py={pageStore.rowHeight}
                        >
                          {group.key}
                        </Td>
                        {availableColumns.map((column) => {
                          const isNumeric = column.isNumeric || false;

                          return (
                            <Td
                              key={column.key}
                              px={4}
                              py={pageStore.rowHeight}
                              textAlign={isNumeric ? "right" : "left"}
                            >
                              {renderSummaryCell(column, group.totals)}
                            </Td>
                          );
                        })}
                      </Tr>

                      {expandedGroups[group.key] && group.hasSubGroups && group.subGroups.map((subGroup) => {
                        const subKey = `${group.key}__${subGroup.key}`;
                        return (
                          <React.Fragment key={subKey}>
                            <Tr bg="#444">
                              <Td>
                                <Button
                                  color="white"
                                  fontSize="20px"
                                  _hover={{ bg: "black", color: "white" }}
                                  size="sm"
                                  bgColor="#444"
                                  onClick={() => toggleGroup(subKey)}
                                >
                                  {expandedGroups[subKey] ? "−" : "+"}
                                </Button>
                              </Td>
                              <Td
                                cursor="pointer"
                                onClick={() => toggleGroup(subKey)}
                                fontWeight="bold"
                                pl={12}
                                py={pageStore.rowHeight}
                              >
                                {subGroup.key}
                              </Td>
                              {availableColumns.map((column) => {
                                const isNumeric = column.isNumeric || false;

                                return (
                                  <Td
                                    key={column.key}
                                    px={4}
                                    py={pageStore.rowHeight}
                                    textAlign={isNumeric ? "right" : "left"}
                                  >
                                    {renderSummaryCell(column, subGroup.totals)}
                                  </Td>
                                );
                              })}
                            </Tr>

                            {expandedGroups[subKey] && subGroup.items.map((row, index) => (
                              <Tr key={`${subKey}-${index}`} bg={index % 2 === 0 ? "#555" : "#666"}>
                                <Td></Td>
                                <Td></Td>
                                {availableColumns.map((column) => {
                                  const isNumeric = column.isNumeric || false;
                                  const isPercentage = column.isPercentage || false;
                                  const value = row[column.key] || (isNumeric ? 0 : "");

                                  return (
                                    <Td
                                      key={column.key}
                                      px={4}
                                      py={2}
                                      textAlign={isNumeric ? "right" : "left"}
                                      fontStyle={row.active ? "normal" : "italic"}
                                      {...textCellAttributes(value, column)}
                                    >
                                      {column.isLink ?
                                        renderCellContent(column, value) :
                                        formatCellValue(value, isNumeric, isPercentage)
                                      }
                                    </Td>
                                  );
                                })}
                              </Tr>
                            ))}
                          </React.Fragment>
                        );
                      })}

                      {expandedGroups[group.key] && !group.hasSubGroups && group.items.map((row, index) => (
                        <Tr key={index} bg={index % 2 === 0 ? "#555" : "#666"}>
                          <Td></Td>
                          <Td></Td>
                          {availableColumns.map((column) => {
                            const isNumeric = column.isNumeric || false;
                            const isPercentage = column.isPercentage || false;
                            const value = row[column.key] || (isNumeric ? 0 : "");

                            return (
                              <Td
                                key={column.key}
                                px={4}
                                py={2}
                                textAlign={isNumeric ? "right" : "left"}
                                fontStyle={row.active ? "normal" : "italic"}
                                {...textCellAttributes(value, column)}
                              >
                                {column.isLink ?
                                  renderCellContent(column, value) :
                                  formatCellValue(value, isNumeric, isPercentage)
                                }
                              </Td>
                            );
                          })}
                        </Tr>
                      ))}
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Table tableLayout="fixed" width="100%" minWidth="800px" variant="simple" bg={""} color={"white"}>
                <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
                  <Tr>
                    {availableColumns.map((column) => (
                      <Th
                        key={column.key}
                        color={"white"}
                        onClick={() => handleSort(column.key)}
                        cursor="pointer"
                        px={4}
                        py={pageStore.rowHeight}
                      >
                        <HeaderTitle
                          title={column.title}
                          suffix={sortConfig.key === column.key && (
                            <Box as="span" ml={2}>
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </Box>
                          )}
                          filter={renderValueFilter(column)}
                        />
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr bg="blue.700" fontWeight="bold">
                    {availableColumns.map((column, index) => {
                      const isNumeric = column.isNumeric || false;

                      return (
                        <Td
                          key={column.key}
                          px={4}
                          py={pageStore.rowHeight}
                          textAlign={isNumeric ? "right" : "left"}
                        >
                          {index === 0
                            ? `Итого ${sortedData.length}`
                            : renderSummaryCell(column, calculateTotals)}
                        </Td>
                      );
                    })}
                  </Tr>

                  {sortedData.map((row, index) => (
                    <Tr key={index} bg={index % 2 == 0 ? "#444" : "#333"}>
                      {availableColumns.map((column) => {
                        const isNumeric = column.isNumeric || false;
                        const isPercentage = column.isPercentage || false;
                        const value = row[column.key] || (isNumeric ? 0 : "");

                        return (
                          <Td
                            key={column.key}
                            px={4}
                            py={pageStore.rowHeight}
                            textAlign={isNumeric ? "right" : "left"}
                            fontStyle={row.active ? "normal" : "italic"}
                            {...textCellAttributes(value, column)}
                          >
                            {column.isLink ?
                              renderCellContent(column, value) :
                              formatCellValue(value, isNumeric, isPercentage)
                            }
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </Box>
        : null}
    </>
  );
});

export default PotentialTable;